import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, SeverityChip, StatusBadge, Confidence, SlaBadge, KevBadge, Spinner } from "@/components/Kit";

const STATUSES = ["Open", "In Progress", "Risk Accepted", "False Positive", "Remediated"];

export default function FindingDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [status, setStatus] = useState("");
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => http.get(`/findings/${id}`, { params: { org_id: orgId } }).then((r) => {
    setF(r.data); setStatus(r.data.status); setAssignee(r.data.assigned_to || "");
  });
  useEffect(() => { if (orgId) load(); }, [id, orgId]);

  if (!f) return <div className="p-8 text-[#8A8175]">Loading…</div>;

  const save = async () => {
    if (status === "Risk Accepted" && !note.trim()) { toast.error("A note is required to accept risk."); return; }
    setBusy(true);
    try {
      await http.patch(`/findings/${id}`, { status, assigned_to: assignee, note: note || undefined }, { params: { org_id: orgId } });
      toast.success("Finding updated"); setNote(""); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto fade-up">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-[#8A8175] hover:text-[#A5300F] mb-3">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <div className="flex items-start gap-3 mb-5">
        <SeverityChip severity={f.severity} />
        {f.kev && <KevBadge />}
        <div>
          <h1 className="font-display text-2xl font-bold text-[#323232]">{f.title}</h1>
          <p className="text-sm text-[#8A8175]">{f.application?.name} · {f.category.toUpperCase()} · <Confidence label={f.confidence} /></p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">
          <Card className="p-5">
            <SectionTitle>Description</SectionTitle>
            <p className="text-sm text-[#4a453d] mt-2">{f.description || "No description."}</p>
            {f.evidence && <>
              <div className="text-[11px] uppercase text-[#8A8175] font-semibold mt-4 mb-1">Evidence</div>
              <pre className="bg-[#F6F1EA] border border-[#ECE6DD] rounded p-3 text-xs font-mono whitespace-pre-wrap overflow-x-auto">{f.evidence}</pre>
            </>}
            {(f.references || []).length > 0 && <>
              <div className="text-[11px] uppercase text-[#8A8175] font-semibold mt-4 mb-1">References</div>
              <ul className="text-xs space-y-1">{f.references.map((r, i) => (
                <li key={`${r}-${i}`}><a href={r} target="_blank" rel="noreferrer" className="text-[#A5300F] underline break-all">{r}</a></li>))}</ul>
            </>}
          </Card>

          <Card className="p-5">
            <SectionTitle>Triage History</SectionTitle>
            <div className="mt-2 space-y-2">
              {(f.triage_notes || []).length === 0 && <div className="text-sm text-[#B5AC9E]">No triage notes yet.</div>}
              {(f.triage_notes || []).map((n, i) => (
                <div key={`${n.at}-${i}`} className="border-l-2 border-[#E7D9C6] pl-3 py-1">
                  <div className="text-xs text-[#8A8175]">{n.author} · {new Date(n.at).toLocaleString("en-GB")} · <span className="font-semibold">{n.status}</span></div>
                  <div className="text-sm">{n.note}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 space-y-3">
            <SectionTitle>Details</SectionTitle>
            <KV k="Status" v={<StatusBadge status={f.status} />} />
            <KV k="SLA" v={<SlaBadge status={f.sla_status} />} />
            <KV k="Age" v={`${f.age_days} days`} />
            <KV k="Due" v={f.due_date ? new Date(f.due_date).toLocaleDateString("en-GB") : "—"} />
            <KV k="CVE" v={(f.cve_ids || [])[0] || "—"} />
            <KV k="CVSS" v={f.cvss_score ?? "—"} />
            <KV k="EPSS" v={f.epss_score != null ? `${(f.epss_score * 100).toFixed(2)}%` : "—"} />
            <KV k="Package" v={f.package_name ? `${f.package_name}@${f.package_version}` : "—"} />
            <KV k="Fixed in" v={f.fixed_version || "None"} />
          </Card>

          <Card className="p-5 space-y-3">
            <SectionTitle>Triage</SectionTitle>
            <div>
              <label className="text-[11px] uppercase text-[#8A8175] font-semibold">Status</label>
              <select data-testid="triage-status" value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase text-[#8A8175] font-semibold">Assign to</label>
              <input data-testid="triage-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}
                className="w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[#8A8175] font-semibold">Note {status === "Risk Accepted" && <span className="text-[#A5300F]">(required)</span>}</label>
              <textarea data-testid="triage-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm" />
            </div>
            <button onClick={save} disabled={busy} data-testid="triage-save"
              className="w-full bg-[#A5300F] text-white font-semibold py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Spinner />} Save
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}

const KV = ({ k, v }) => (
  <div className="flex justify-between items-center gap-2 text-sm"><span className="text-[#8A8175]">{k}</span><span className="font-mono text-right">{v}</span></div>
);
